/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ButtonGroup,
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  useToast,
  VStack,
  Text,
  HStack,
  Badge,
} from '@chakra-ui/react';
import { PublicKey } from '@solana/web3.js';
import { useChainManager } from '../../chain/hooks/useChainManager';
import { chainManager } from '../../chain';
import { SPLTokenInfo } from '../../chain/solana/spl-token';
import React, { useEffect, useState } from 'react';
import { CardInfo } from '../CardInfo';
import { NotchedButton } from '../NotchedButton';
import { PaymentSummary } from '../PaymentSummary';

interface SolanaExtraTokenFormProps {
  goBack: () => void;
  onCreateCampaign: () => Promise<void> | void;
  onTokenChange: (token: SPLTokenInfo | null, amount: number) => void;
}

export const SolanaExtraTokenForm: React.FC<SolanaExtraTokenFormProps> = ({
  goBack,
  onCreateCampaign,
  onTokenChange,
}) => {
  const { currentChain, wallet } = useChainManager();
  const [selectedToken, setSelectedToken] = useState<SPLTokenInfo | null>(null);
  const [amountOnItem, setAmountOnItem] = useState<number>(0);
  const [isOtherToken, setIsOtherToken] = useState(false);
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);
  const [isFetchingTokenInfo, setIsFetchingTokenInfo] = useState(false);
  const [customTokenAddress, setCustomTokenAddress] = useState<string>('');
  const [commonTokens, setCommonTokens] = useState<
    Record<string, SPLTokenInfo>
  >({});
  const [tokenBalance, setTokenBalance] = useState<number>(0);

  const toast = useToast({
    duration: 2000,
    isClosable: true,
    position: 'bottom',
  });

  // Load common SPL tokens
  useEffect(() => {
    const loadCommonTokens = async () => {
      if (currentChain !== 'solana') return;

      try {
        const tokens = await chainManager.getCommonSPLTokens();
        setCommonTokens(tokens);
      } catch (error) {
        console.error('Failed to load common tokens:', error);
      }
    };

    loadCommonTokens();
  }, [currentChain]);

  // Load token balance when token changes
  useEffect(() => {
    const loadTokenBalance = async () => {
      if (!selectedToken) {
        setTokenBalance(0);
        return;
      }

      // Reset balance first to show loading state
      setTokenBalance(0);

      try {
        const balance = await chainManager.getSPLTokenBalance(
          selectedToken.mint.toString(),
        );
        setTokenBalance(balance);
      } catch (error: any) {
        // Silently handle errors - balance will remain 0
        // Errors are already logged in the adapter
        console.warn('Token balance check failed:', error?.message || error);
        setTokenBalance(0);
      }
    };

    loadTokenBalance();
  }, [selectedToken, chainManager]);

  // Notify parent component of token changes
  useEffect(() => {
    onTokenChange(selectedToken, amountOnItem);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedToken, amountOnItem]);
  // Note: onTokenChange is intentionally excluded from deps to prevent infinite loops
  // It's a callback from parent and should be stable, but if it changes we still want to update

  const handleSelectTokenChange = (ev: any) => {
    setAmountOnItem(0);

    if (ev.target.value === 'other') {
      setSelectedToken(null);
      setIsOtherToken(true);
      return;
    }

    if (ev.target.value === '') {
      setSelectedToken(null);
      setIsOtherToken(false);
      return;
    }

    setIsOtherToken(false);
    const token = commonTokens[ev.target.value];
    setSelectedToken(token || null);
  };

  const handleFetchCustomToken = async () => {
    if (!customTokenAddress || !wallet) return;

    try {
      setIsFetchingTokenInfo(true);

      // Validate address
      new PublicKey(customTokenAddress);

      const tokenInfo = await chainManager.getSPLTokenInfo(customTokenAddress);
      if (!tokenInfo) {
        throw new Error('Token not found');
      }

      setSelectedToken(tokenInfo);
    } catch (error) {
      console.error('Failed to fetch token info:', error);
      toast({
        title: 'Error',
        description: 'Invalid token address or token not found',
        status: 'error',
      });
    } finally {
      setIsFetchingTokenInfo(false);
    }
  };

  const handleSubmit = () => {
    setIsSubmitClicked(true);
  };

  const handleGoBack = () => {
    goBack();
  };

  // Handle form submission
  useEffect(() => {
    const handleFormSubmit = async () => {
      if (!isSubmitClicked) return;

      try {
        await onCreateCampaign();
      } catch (error) {
        console.error('Form submission error:', error);
      } finally {
        setIsSubmitClicked(false);
      }
    };

    handleFormSubmit();
  }, [isSubmitClicked, onCreateCampaign]);

  const isSOL = !selectedToken;
  const tokenSymbol = isSOL ? 'SOL' : selectedToken?.symbol || '';
  const tokenName = isSOL ? 'Solana' : selectedToken?.name || '';

  return (
    <VStack spacing={6} align="stretch">
      <CardInfo
        heading="Thể lệ tham gia Jackpot (Solana)"
        content={[
          'Mỗi bao LixiNFT có kèm 1 vé sổ xố kiến thiết miền Mê(taverse), với giá 0.01 SOL. Bạn có thể kèm thêm SPL token trong mỗi bao.',
          'Bạn cần giữ NFT trong ví để có thể tham gia sổ xố.',
        ]}
      />

      <FormControl>
        <FormLabel fontSize="20px" fontWeight="600">
          Đơn vị tiền kèm theo
        </FormLabel>
        <Select
          placeholder="Chọn token kèm theo (tùy chọn)"
          value={
            isOtherToken
              ? 'other'
              : selectedToken
              ? Object.keys(commonTokens).find(
                  key =>
                    commonTokens[key].mint.toString() ===
                    selectedToken.mint.toString(),
                )
              : ''
          }
          onChange={handleSelectTokenChange}
          bg="white"
          color="darkBg"
        >
          <option value="">Chỉ dùng SOL (không kèm token)</option>
          {Object.entries(commonTokens).map(([key, token]) => (
            <option key={key} value={key}>
              {token.symbol} - {token.name}
            </option>
          ))}
          <option value="other">Token khác</option>
        </Select>
      </FormControl>

      {isOtherToken && (
        <FormControl>
          <FormLabel fontSize="20px" fontWeight="600">
            Nhập địa chỉ SPL Token
          </FormLabel>
          <HStack>
            <Input
              placeholder="Nhập mint address của SPL token"
              value={customTokenAddress}
              onChange={e => setCustomTokenAddress(e.target.value)}
              bg="white"
              color="darkBg"
            />
            <NotchedButton
              bgColor="primary"
              color="white"
              onClick={handleFetchCustomToken}
              isLoading={isFetchingTokenInfo}
              loadingText="Loading..."
              isDisabled={!customTokenAddress}
            >
              Xác nhận
            </NotchedButton>
          </HStack>
        </FormControl>
      )}

      {selectedToken && (
        <VStack
          spacing={2}
          align="stretch"
          p={4}
          bg="gray.50"
          borderRadius="md"
        >
          <HStack justify="space-between">
            <Text fontWeight="bold">Token Info:</Text>
            <Badge colorScheme="blue">{tokenSymbol}</Badge>
          </HStack>
          <Text fontSize="sm" color="gray.600">
            Name: {tokenName}
          </Text>
          <Text fontSize="sm" color="gray.600">
            Mint: {selectedToken.mint.toString()}
          </Text>
          <Text fontSize="sm" color="gray.600">
            Decimals: {selectedToken.decimals}
          </Text>
          <Text fontSize="sm" color="gray.600">
            Balance: {tokenBalance.toFixed(6)} {tokenSymbol}
          </Text>
        </VStack>
      )}

      <FormControl>
        <FormLabel fontSize="20px" fontWeight="600">
          Số {tokenSymbol} trong 1 bao
        </FormLabel>
        <NumberInput
          value={amountOnItem}
          onChange={val => setAmountOnItem(parseFloat(val || '0'))}
          min={0}
          precision={isSOL ? 9 : selectedToken?.decimals || 6}
        >
          <NumberInputField
            placeholder={`Nhập số lượng ${tokenSymbol}`}
            bg="white"
            color="darkBg"
          />
        </NumberInput>
        {selectedToken && (
          <Text fontSize="sm" color="gray.500" mt={1}>
            Số dư hiện tại: {tokenBalance.toFixed(6)} {tokenSymbol}
          </Text>
        )}
      </FormControl>

      <PaymentSummary
        heading="Tổng thanh toán"
        lixiAmount={1} // This should come from parent component
        giftToken={amountOnItem}
        giftTokenSymbol={tokenSymbol}
      />

      <ButtonGroup w="full" flexDirection="column-reverse">
        <NotchedButton
          bgColor="primary"
          color="white"
          marginTop="5"
          width="100%"
          onClick={handleGoBack}
        >
          Quay lại
        </NotchedButton>
        <NotchedButton
          bgColor="primary"
          color="white"
          marginTop="5"
          marginLeft="0 !important"
          width="100%"
          onClick={handleSubmit}
          isDisabled={!!(selectedToken && amountOnItem > tokenBalance)}
        >
          {selectedToken && amountOnItem > tokenBalance
            ? 'Không đủ số dư'
            : 'Thanh toán'}
        </NotchedButton>
      </ButtonGroup>
    </VStack>
  );
};

export default SolanaExtraTokenForm;
